import { parse } from "csv-parse/sync";

export function csvToArray(csv: string, sep = ","): string[][] {
  return parse(csv, { delimiter: sep });
}

export function arrayToCsv(array: string[][], sep = ",", quote = true) {
  if (array) {
    return array
      .map((row) => {
        let cols = row;
        if (quote) {
          cols = cols.map((col) => col.replace(/"/g, '""'));
          const content = cols.join(`"${sep}"`);
          return `"${content}"`;
        }
        return cols.join(sep);
      })
      .join("\n");
  }
  return "";
}

interface ColumnInfo {
  jsonCellCount: number;
  jsonKeys: Set<string>;
  jsonObjects: { [index: number]: any };
}

export function expandJsonInCsv(csv: string[][]): string[][] {
  const header = csv[0];
  const columnInfos = getColumnInfos(csv);
  const newHeader: string[] = [];
  const newCsv: string[][] = [];
  for (let i = 0; i < header.length; i++) {
    const columnInfo = columnInfos[i];
    if (columnInfo.jsonCellCount > 0 && columnInfo.jsonKeys.size > 0) {
      for (const key of columnInfo.jsonKeys) {
        newHeader.push(key);
      }
    } else {
      newHeader.push(header[i]);
    }
  }
  for (let i = 1; i < csv.length; i++) {
    const row = csv[i];
    const newRow: string[] = [];
    for (let j = 0; j < header.length; j++) {
      const columnInfo = columnInfos[j];
      if (columnInfo.jsonCellCount > 0 && columnInfo.jsonKeys.size > 0) {
        const cell = row[j];
        if (!cell || columnInfo.jsonObjects[i - 1] == undefined) {
          for (const _ of columnInfo.jsonKeys) {
            newRow.push("");
          }
          continue;
        }
        const obj = columnInfo.jsonObjects[i - 1];
        const keysAndValues = expandJsonKeyAndValue(obj);
        const keysAndValuesMap = new Map<string, string>(keysAndValues);
        for (const key of columnInfo.jsonKeys) {
          const value = keysAndValuesMap.get(key);
          if (value) {
            newRow.push(value);
          } else {
            newRow.push("");
          }
        }
      } else {
        newRow.push(row[j]);
      }
    }
    newCsv.push(newRow);
  }
  return [newHeader, ...newCsv];
}

function getColumnInfos(csv: string[][]): ColumnInfo[] {
  const header = csv[0];
  const columnInfos: ColumnInfo[] = [];
  for (let i = 0; i < header.length; i++) {
    let jsonCellCount = 0;
    const jsonKeys = new Set<string>();
    const jsonObjects: { [index: number]: any } = {};
    for (let j = 1; j < csv.length; j++) {
      const cell = csv[j][i];
      if (!cell) {
        jsonCellCount++;
        continue;
      }
      if (cell.startsWith("{") && cell.endsWith("}")) {
        try {
          const obj = JSON.parse(cell);
          const keys = expandJsonObjKeys(obj);
          for (const key of keys) {
            jsonKeys.add(key);
          }
          jsonObjects[j - 1] = obj;
          jsonCellCount++;
        } catch (e) {
          // noop
        }
      }
    }
    columnInfos.push({ jsonCellCount, jsonKeys, jsonObjects });
  }
  return columnInfos;
}

function expandJsonObjKeys(obj: Record<string, any>): string[] {
  const keys = Object.keys(obj);
  const newKeys = [];
  for (const key of keys) {
    if (typeof obj[key] === "object") {
      if (obj[key] == null) {
        newKeys.push(key);
        continue;
      }
      const subKeys = expandJsonObjKeys(obj[key]);
      for (const subKey of subKeys) {
        newKeys.push(`${key}.${subKey}`);
      }
    } else {
      newKeys.push(key);
    }
  }
  return newKeys;
}

function expandJsonKeyAndValue(obj: Record<string, any>): [string, string][] {
  const keys = Object.keys(obj);
  const newKeysAndValues: [string, string][] = [];
  for (const key of keys) {
    if (typeof obj[key] === "object") {
      if (obj[key] == null) {
        newKeysAndValues.push([key, "null"]);
        continue;
      }
      const subKeysAndValues = expandJsonKeyAndValue(obj[key]);
      for (const [subKey, subValue] of subKeysAndValues) {
        newKeysAndValues.push([`${key}.${subKey}`, subValue]);
      }
    } else {
      const value = obj[key];
      switch (typeof value) {
        case "string":
        case "number":
        case "boolean":
          newKeysAndValues.push([key, String(value)]);
          break;
        case "object":
          if (value === null) {
            newKeysAndValues.push([key, "null"]);
          } else {
            newKeysAndValues.push([key, JSON.stringify(value)]);
          }
          break;
      }
    }
  }
  return newKeysAndValues;
}
